// src/pages/Appointments/AppointmentForm/AppointmentForm.jsx
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useAppointmentForm } from "./appointmentFormHooks";
import Button from "../../../components/common/Button";
import {
  CustomerSelectionSection,
  BranchSelectionSection,
  AppointmentTypeSection,
  IntentSection,
  ContactInfoSection,
  CartSummarySection,
} from "./AppointmentFormSections";
import { DateTimeSelectionSection } from "./DateTimeSelectionSection";
import { getAvailableSlotsForWeekThunk } from "../../../store/thunks/appointmentThunks";
import {
  FiArrowLeft,
  FiArrowRight,
  FiCheck,
  FiUser,
  FiMapPin,
  FiCalendar,
} from "react-icons/fi";

const AppointmentForm = ({ mode = "add" }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { branches } = useSelector((state) => state.branch);
  const { weekSlots, loadingSlots } = useSelector((state) => state.appointment);
  const { cart } = useSelector((state) => state.cart);

  const {
    formData,
    errors,
    submitting,
    step,
    setStep,
    handleChange,
    handleNestedChange,
    handleArrayChange,
    handleSubmit,
    validateStep,
    canProceedToNextStep,
    handleCustomerSelect,
    loadingAppointment,
    mode: currentMode,
    serverError,
  } = useAppointmentForm(mode);

  // ✅ CRITICAL: Show loading spinner while fetching appointment data
  if (loadingAppointment) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-neutral-dark text-lg">
            Loading appointment details...
          </p>
        </div>
      </div>
    );
  }

  // Handle week slots loading
  const handleWeekChange = (dates) => {
    if (formData.branchCode && formData.duration) {
      dispatch(
        getAvailableSlotsForWeekThunk({
          branchCode: formData.branchCode,
          dates,
          duration: formData.duration,
        })
      );
    }
  };

  // Determine step titles based on mode
  const stepTitles = {
    1:
      currentMode === "reschedule"
        ? "Appointment Details"
        : "Customer & Details",
    2: "Date & Time",
    3: "Intent & Preferences",
    4: "Review & Confirm",
  };

  const handleNext = () => {
    if (validateStep(step)) {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    setStep(step - 1);
  };

  return (
    <div className="container mx-auto p-4 md:p-6 max-w-5xl">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-neutral-dark hover:text-primary mb-4 transition-colors"
        >
          <FiArrowLeft className="mr-2" />
          Back to Appointments
        </button>

        {/* ✅ Dynamic title based on mode */}
        <h1 className="text-2xl md:text-3xl font-bold text-text">
          {currentMode === "reschedule"
            ? "Reschedule Appointment"
            : "Book New Appointment"}
        </h1>
        <p className="text-neutral-dark mt-2">
          {currentMode === "reschedule"
            ? "Update the appointment date and time"
            : "Schedule a customer appointment"}
        </p>
      </div>

      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          {[1, 2, 3, 4].map((s) => (
            <React.Fragment key={s}>
              <div
                className={`flex items-center justify-center w-10 h-10 rounded-full font-semibold transition-colors ${
                  step >= s
                    ? "bg-primary text-white"
                    : "bg-neutral-light text-neutral-dark"
                }`}
              >
                {step > s ? <FiCheck /> : s}
              </div>
              {s < 4 && (
                <div
                  className={`flex-1 h-1 mx-2 transition-colors ${
                    step > s ? "bg-primary" : "bg-neutral-light"
                  }`}
                />
              )}
            </React.Fragment>
          ))}
        </div>
        <p className="text-sm text-neutral-dark text-center mt-2">
          Step {step} of 4: {stepTitles[step]}
        </p>
      </div>

      {/* Step Content */}
      <div className="space-y-6">
        {/* ====== STEP 1: Customer & Details ====== */}
        {step === 1 && (
          <>
            {/* ✅ RESCHEDULE MODE: Show customer as read-only */}
            {currentMode === "reschedule" ? (
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center gap-2 mb-4">
                  <FiUser className="text-primary text-xl" />
                  <h3 className="text-lg font-semibold text-text">Customer</h3>
                  <span className="text-xs bg-neutral-light px-2 py-1 rounded">
                    Read-only
                  </span>
                </div>
                <div className="p-4 bg-neutral-light rounded-lg">
                  <p className="font-medium text-text text-lg">
                    {formData.selectedCustomer?.name || "Unknown Customer"}
                  </p>
                  <p className="text-sm text-neutral-dark mt-1">
                    📱 {formData.selectedCustomer?.phoneNumber || "N/A"}
                  </p>
                  {formData.selectedCustomer?.email && (
                    <p className="text-sm text-neutral-dark">
                      ✉️ {formData.selectedCustomer.email}
                    </p>
                  )}
                  {formData.selectedCustomer?.userCode && (
                    <p className="text-xs text-neutral-dark mt-2">
                      ID: {formData.selectedCustomer.userCode}
                    </p>
                  )}
                </div>
              </div>
            ) : (
              // ADD MODE: Show customer selection
              <CustomerSelectionSection
                formData={formData}
                errors={errors}
                onChange={handleChange}
                onCustomerSelect={handleCustomerSelect}
              />
            )}

            {/* ✅ RESCHEDULE MODE: Show branch as read-only */}
            {currentMode === "reschedule" ? (
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center gap-2 mb-4">
                  <FiMapPin className="text-primary text-xl" />
                  <h3 className="text-lg font-semibold text-text">Branch</h3>
                  <span className="text-xs bg-neutral-light px-2 py-1 rounded">
                    Read-only
                  </span>
                </div>
                <div className="p-4 bg-neutral-light rounded-lg">
                  <p className="font-medium text-text text-lg">
                    {formData.branchCode}
                  </p>
                </div>
              </div>
            ) : (
              // ADD MODE: Show branch selection
              <BranchSelectionSection
                formData={formData}
                errors={errors}
                onChange={handleChange}
                branches={branches}
              />
            )}

            {/* ✅ RESCHEDULE MODE: Show type as read-only */}
            {currentMode === "reschedule" ? (
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center gap-2 mb-4">
                  <FiCalendar className="text-primary text-xl" />
                  <h3 className="text-lg font-semibold text-text">
                    Appointment Type
                  </h3>
                  <span className="text-xs bg-neutral-light px-2 py-1 rounded">
                    Read-only
                  </span>
                </div>
                <div className="p-4 bg-neutral-light rounded-lg">
                  <p className="font-medium text-text text-lg">
                    {formData.type?.replace(/_/g, " ") || "Unknown Type"}
                  </p>
                  <p className="text-sm text-neutral-dark mt-1">
                    Duration: {formData.duration} minutes
                  </p>
                </div>
              </div>
            ) : (
              // ADD MODE: Show type selection
              <AppointmentTypeSection
                formData={formData}
                errors={errors}
                onChange={handleChange}
              />
            )}
          </>
        )}

        {/* ====== STEP 2: Date & Time (ALWAYS EDITABLE) ====== */}
        {step === 2 && (
          <>
            <DateTimeSelectionSection
              formData={formData}
              errors={errors}
              onChange={handleChange}
              weekSlots={weekSlots}
              loadingSlots={loadingSlots}
              onWeekChange={handleWeekChange}
            />

            {/* ✅ RESCHEDULE REASON INPUT (Only in reschedule mode) */}
            {currentMode === "reschedule" && (
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center gap-2 mb-4">
                  <FiCalendar className="text-primary text-xl" />
                  <h3 className="text-lg font-semibold text-text">
                    Reason for Rescheduling
                  </h3>
                  <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded">
                    Required
                  </span>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Please provide a reason for rescheduling this appointment *
                  </label>
                  <textarea
                    name="rescheduleReason"
                    value={formData.rescheduleReason}
                    onChange={handleChange}
                    rows="3"
                    placeholder="E.g., Customer requested time change, Admin unavailable, Store maintenance, etc."
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary transition-colors ${
                      errors.rescheduleReason
                        ? "border-red-500 focus:ring-red-500"
                        : "border-gray-300"
                    }`}
                  />
                  {errors.rescheduleReason && (
                    <p className="mt-2 text-sm text-red-500">
                      {errors.rescheduleReason}
                    </p>
                  )}
                  <p className="mt-2 text-xs text-neutral-dark">
                    This reason will be recorded in the appointment history and
                    may be shared with the customer.
                  </p>
                </div>
              </div>
            )}
          </>
        )}

        {/* ====== STEP 3: Intent & Preferences ====== */}
        {step === 3 && (
          <>
            {currentMode === "reschedule" ? (
              // ✅ RESCHEDULE MODE: Show intent as read-only
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center gap-2 mb-4">
                  <FiCalendar className="text-primary text-xl" />
                  <h3 className="text-lg font-semibold text-text">
                    Original Intent Details
                  </h3>
                  <span className="text-xs bg-neutral-light px-2 py-1 rounded">
                    Read-only
                  </span>
                </div>
                <div className="p-4 bg-neutral-light rounded-lg space-y-3">
                  {formData.intent?.purpose && (
                    <div>
                      <span className="text-sm font-medium text-neutral-dark">
                        Purpose:
                      </span>
                      <p className="text-text">{formData.intent.purpose}</p>
                    </div>
                  )}
                  {formData.intent?.occasion && (
                    <div>
                      <span className="text-sm font-medium text-neutral-dark">
                        Occasion:
                      </span>
                      <p className="text-text">{formData.intent.occasion}</p>
                    </div>
                  )}
                  {formData.intent?.budget?.min && (
                    <div>
                      <span className="text-sm font-medium text-neutral-dark">
                        Budget:
                      </span>
                      <p className="text-text">
                        ₹{formData.intent.budget.min?.toLocaleString("en-IN")} -
                        ₹{formData.intent.budget.max?.toLocaleString("en-IN")}
                      </p>
                    </div>
                  )}
                  {formData.intent?.customerNotes && (
                    <div>
                      <span className="text-sm font-medium text-neutral-dark">
                        Notes:
                      </span>
                      <p className="text-text">
                        {formData.intent.customerNotes}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              // ADD MODE: Show full intent form
              <IntentSection
                formData={formData}
                errors={errors}
                onChange={handleChange}
                onNestedChange={handleNestedChange}
                onArrayChange={handleArrayChange}
              />
            )}
          </>
        )}

        {/* ====== STEP 4: Review & Confirm ====== */}
        {step === 4 && (
          <div className="space-y-6">
            {/* Summary Card */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-text mb-4">
                {currentMode === "reschedule"
                  ? "Reschedule Summary"
                  : "Appointment Summary"}
              </h3>

              <div className="space-y-4">
                {/* Customer Info */}
                <div className="border-b pb-4">
                  <h4 className="text-sm font-medium text-neutral-dark mb-2">
                    Customer
                  </h4>
                  <p className="font-medium text-text">
                    {formData.selectedCustomer?.name}
                  </p>
                  <p className="text-sm text-neutral-dark">
                    {formData.contactInfo.phone}
                  </p>
                  {formData.contactInfo.email && (
                    <p className="text-sm text-neutral-dark">
                      {formData.contactInfo.email}
                    </p>
                  )}
                </div>

                {/* Appointment Details */}
                <div className="border-b pb-4">
                  <h4 className="text-sm font-medium text-neutral-dark mb-2">
                    Appointment Details
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-neutral-dark">Branch</p>
                      <p className="font-medium text-text">
                        {formData.branchCode}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-neutral-dark">Type</p>
                      <p className="font-medium text-text">
                        {formData.type?.replace(/_/g, " ")}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-neutral-dark">Date</p>
                      <p className="font-medium text-text">
                        {new Date(formData.selectedDate).toLocaleDateString(
                          "en-IN",
                          {
                            weekday: "long",
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          }
                        )}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-neutral-dark">Time</p>
                      <p className="font-medium text-text">
                        {formData.selectedTime}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-neutral-dark">Duration</p>
                      <p className="font-medium text-text">
                        {formData.duration} minutes
                      </p>
                    </div>
                  </div>
                </div>

                {/* Reschedule Reason (only in reschedule mode) */}
                {currentMode === "reschedule" && formData.rescheduleReason && (
                  <div className="border-b pb-4">
                    <h4 className="text-sm font-medium text-neutral-dark mb-2">
                      Reason for Rescheduling
                    </h4>
                    <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <p className="text-text">{formData.rescheduleReason}</p>
                    </div>
                  </div>
                )}

                {/* Intent (only in add mode or show read-only) */}
                {formData.intent?.purpose && (
                  <div>
                    <h4 className="text-sm font-medium text-neutral-dark mb-2">
                      Intent
                    </h4>
                    <p className="text-text">{formData.intent.purpose}</p>
                    {formData.intent.occasion && (
                      <p className="text-sm text-neutral-dark mt-1">
                        Occasion: {formData.intent.occasion}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Cart Summary (if exists) */}
            {cart && cart.items && cart.items.length > 0 && (
              <CartSummarySection cart={cart} />
            )}

            {serverError && ( // ✅ Display server error if exists
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-700">⚠️ {serverError}</p>
              </div>
            )}

            {/* Confirmation Notice */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                <strong>📧 Confirmation:</strong>{" "}
                {currentMode === "reschedule"
                  ? "Customer will be notified about the rescheduled appointment."
                  : "Customer will receive confirmation via SMS and email."}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Navigation Buttons */}
      <div className="flex gap-4 mt-8">
        {step > 1 && (
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={submitting}
            className="flex-1"
          >
            <FiArrowLeft className="mr-2" />
            Back
          </Button>
        )}

        {step < 4 ? (
          <Button
            onClick={handleNext}
            disabled={!canProceedToNextStep() || submitting}
            className="flex-1"
          >
            Next
            <FiArrowRight className="ml-2" />
          </Button>
        ) : (
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            className="flex-1"
          >
            {submitting ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Processing...
              </div>
            ) : (
              <>
                <FiCheck className="mr-2" />
                {currentMode === "reschedule"
                  ? "Reschedule Appointment"
                  : "Book Appointment"}
              </>
            )}
          </Button>
        )}
      </div>

      {/* Helper Text */}
      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-sm text-blue-800">
          💡 <strong>Tip:</strong>{" "}
          {currentMode === "reschedule"
            ? "Only the date and time can be changed when rescheduling. Other details remain the same."
            : "Fill out all required fields to book an appointment. You can go back to edit previous steps."}
        </p>
      </div>
    </div>
  );
};

export default AppointmentForm;
